// Period select: a worker overwhelmed by seeing ALL planned jobs of their device can narrow the
// list by a time window (Today/This week/This month/This year), applied on top of whatever
// column filters they already have. Pure logic only -- no React here.
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns'

export const PERIOD_KEYS = {
  ALL: 'all',
  TODAY: 'today',
  THIS_WEEK: 'thisWeek',
  THIS_MONTH: 'thisMonth',
  THIS_YEAR: 'thisYear'
}

export const PERIOD_OPTIONS = [
  { key: PERIOD_KEYS.ALL, label: 'All' },
  { key: PERIOD_KEYS.TODAY, label: 'Today' },
  { key: PERIOD_KEYS.THIS_WEEK, label: 'This week' },
  { key: PERIOD_KEYS.THIS_MONTH, label: 'This month' },
  { key: PERIOD_KEYS.THIS_YEAR, label: 'This year' }
]

// The week starts on Monday (RN/UX decision for this feature), not date-fns's Sunday default.
const WEEK_STARTS_ON_MONDAY = { weekStartsOn: 1 }

const DEFAULT_FIELD = 'startDate'
// Same delimiter used by DateColumnFilterPopover.jsx for the `between` operator: a pipe instead of
// a comma, because the pagination request travels as a query string and URLSearchParams joins
// array entries with commas, which would split the value across two slots.
const RANGE_DELIMITER = '|'
const BETWEEN_OPERATOR = 'between'

/**
 * Resolves a period key into a [from, to] local Date range (both instants), or `null` for "all"
 * (nothing to filter by). Local time is used throughout -- `toISOString()` on the resulting Date
 * objects yields the correct absolute instant regardless of the browser's timezone.
 *
 * @param {string} periodKey - One of PERIOD_KEYS
 * @param {Date} [referenceDate] - Defaults to "now"
 * @returns {[Date, Date]|null}
 */
export const getPeriodRange = (periodKey, referenceDate = new Date()) => {
  switch (periodKey) {
    case PERIOD_KEYS.TODAY:
      return [startOfDay(referenceDate), endOfDay(referenceDate)]
    case PERIOD_KEYS.THIS_WEEK:
      return [startOfWeek(referenceDate, WEEK_STARTS_ON_MONDAY), endOfWeek(referenceDate, WEEK_STARTS_ON_MONDAY)]
    case PERIOD_KEYS.THIS_MONTH:
      return [startOfMonth(referenceDate), endOfMonth(referenceDate)]
    case PERIOD_KEYS.THIS_YEAR:
      return [startOfYear(referenceDate), endOfYear(referenceDate)]
    case PERIOD_KEYS.ALL:
    default:
      return null
  }
}

/**
 * Injects the period's date range as a `between` column filter into a pagination request,
 * composing with any pre-existing column filters. `usePagination.jsx` only builds the
 * columnFilters/columnFilterValues/columnFilterOperators arrays when the user already has column
 * filters of their own -- this always creates (or extends) all three together, so they never
 * drift out of parallel. Returns the pagination unchanged for PERIOD_KEYS.ALL (nothing to inject).
 *
 * @param {Object} pagination - The pagination request about to be sent via apiPost
 * @param {string} periodKey - One of PERIOD_KEYS
 * @param {string} [field] - Column field to filter by (defaults to 'startDate')
 * @param {Date} [referenceDate] - Defaults to "now"
 * @returns {Object} A new pagination object (the input is never mutated)
 */
export const injectPeriodFilter = (pagination, periodKey, field = DEFAULT_FIELD, referenceDate = new Date()) => {
  const range = getPeriodRange(periodKey, referenceDate)
  if (!range) return pagination

  const [from, to] = range
  const value = `${from.toISOString()}${RANGE_DELIMITER}${to.toISOString()}`

  const columnFilters = Array.isArray(pagination?.columnFilters) ? [...pagination.columnFilters] : []
  const columnFilterValues = Array.isArray(pagination?.columnFilterValues) ? [...pagination.columnFilterValues] : []
  const columnFilterOperators = Array.isArray(pagination?.columnFilterOperators) ? [...pagination.columnFilterOperators] : []

  columnFilters.push(field)
  columnFilterValues.push(value)
  columnFilterOperators.push(BETWEEN_OPERATOR)

  return { ...pagination, columnFilters, columnFilterValues, columnFilterOperators }
}
